import React from 'react';

interface AdvancedFiltersProps {
  filters: {
    category: string;
    sousCategorie: string;
    status: string;
    couleurPrincipale: string;
    couleurSecondaire: string;
    besoinAchat: string;
  };
  onFilterChange: (name: string, value: string) => void;
  onResetFilters: () => void;
}

const CATEGORIES = [
  'Activités nature',
  'Bible & pédagogie spirituelle',
  'Bureautique',
  'Divers & vie quotidienne',
  'Jeux créé / personnalisés',
  'Jeux de société',
  'Jeux extérieurs',
  'Jouets et jeux d\'enfants',
  'Matériel créatif & manuel',
  'Matériel électronique',
  'Matériel vidéo',
  'Musique, son & lumières',
  'Pédagogie',
  'Sécurité & encadrement'
];

const SOUS_CATEGORIES = [
  'Camping & itinérance',
  'Plage / Lac',
  'Randonnée',
  'Bibles',
  'Fourniture de bureau',
  'Ateliers cuisine',
  'Organisation',
  'Jeux collectifs',
  'Jeux immersifs',
  'Classiques',
  'Coopératifs / éducatifs',
  'Baby-foot / ping-pong',
  'Ballons & sports',
  'Jouets',
  'Arts plastiques',
  'Décors & costumes',
  'Travaux manuels'
];

const ETATS = [
  { value: 'bon', label: 'Bon' },
  { value: 'à réparer', label: 'À réparer' },
  { value: 'incomplet', label: 'Incomplet' },
  { value: 'neuf', label: 'Neuf' },
  { value: 'usé', label: 'Usé' },
  { value: 'hs', label: 'Hors service' }
];

const COULEURS = [
  'Rouge', 'Bleu', 'Vert', 'Jaune', 'Orange', 'Violet', 'Rose', 'Marron',
  'Noir', 'Blanc', 'Gris', 'Beige', 'Turquoise', 'Indigo', 'Lime'
];

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters,
  onFilterChange,
  onResetFilters
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filtres avancés</h3>
        <button
          onClick={onResetFilters}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Réinitialiser
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Catégorie */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Catégorie
          </label>
          <select
            id="category"
            value={filters.category}
            onChange={(e) => onFilterChange('category', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes les catégories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Sous-catégorie */}
        <div>
          <label htmlFor="sousCategorie" className="block text-sm font-medium text-gray-700 mb-1">
            Sous-catégorie
          </label>
          <select
            id="sousCategorie"
            value={filters.sousCategorie}
            onChange={(e) => onFilterChange('sousCategorie', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes les sous-catégories</option>
            {SOUS_CATEGORIES.map(sousCat => (
              <option key={sousCat} value={sousCat}>{sousCat}</option>
            ))}
          </select>
        </div>

        {/* État */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            État
          </label>
          <select
            id="status"
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tous les états</option>
            {ETATS.map(etat => (
              <option key={etat.value} value={etat.value}>{etat.label}</option>
            ))}
          </select>
        </div>

        {/* Couleur principale */}
        <div>
          <label htmlFor="couleurPrincipale" className="block text-sm font-medium text-gray-700 mb-1">
            Couleur principale
          </label>
          <select
            id="couleurPrincipale"
            value={filters.couleurPrincipale}
            onChange={(e) => onFilterChange('couleurPrincipale', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes les couleurs</option>
            {COULEURS.map(couleur => (
              <option key={couleur} value={couleur}>{couleur}</option>
            ))}
          </select>
        </div>

        {/* Couleur secondaire */}
        <div>
          <label htmlFor="couleurSecondaire" className="block text-sm font-medium text-gray-700 mb-1">
            Couleur secondaire
          </label>
          <select
            id="couleurSecondaire"
            value={filters.couleurSecondaire}
            onChange={(e) => onFilterChange('couleurSecondaire', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Toutes les couleurs</option>
            {COULEURS.map(couleur => (
              <option key={couleur} value={couleur}>{couleur}</option>
            ))}
          </select>
        </div>

        {/* Besoin d'achat */}
        <div>
          <label htmlFor="besoinAchat" className="block text-sm font-medium text-gray-700 mb-1">
            Besoin d'achat
          </label>
          <select
            id="besoinAchat"
            value={filters.besoinAchat}
            onChange={(e) => onFilterChange('besoinAchat', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tous</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;



